import dayjs from 'dayjs'
import { useApiClient } from '@/composables/useApiClient'
import { ProductSalesMode } from '@/view-models/product'
import { BookingTimeSlotType } from '@/view-models/booking'

export const state = () => ({
  pid: null,
  productInfo: {},
  productList: [],
  otherProducts: [],
  commentTagsList: [],
  maxCrossDays: {},
  dayPrices: [],
  categoryProductInfo: { products: [], totalCount: 0 },
})

export const getters = {
  productInfo: state => state.productInfo || {},
  productList: state => state.productList,
  otherProducts: state => state.otherProducts,
  commentTagsList: state => state.commentTagsList,
  maxCrossDays: state => state.maxCrossDays,
  dayPrices: state => state.dayPrices,
  categoryProductInfo: state => state.categoryProductInfo,
}

export const actions = {
  async getProductInfo({ commit }, { pid, params = {}, isCdnApiRequestedByPath = false }) {
    const url = `/pub/product/${pid}/info`
    const apiClient = useApiClient()
    const res = await apiClient.$get(url, { params: params })

    if (res.code !== 0) {
      return res
    }

    let productResult = res.data

    const urlParam = isCdnApiRequestedByPath ? res.data.static_link.split('v2/funnow')[1] : res.data.static_link
    const staticLinkRes = urlParam
      ? await apiClient
          .$get(urlParam, {
            credentials: 'omit',
          })
          .catch(error => {
            console.error('getProductInfo static: error ', error)
            return { code: 0, data: {} }
          })
      : { code: 0, data: {} }

    productResult = {
      ...productResult,
      productIntro: staticLinkRes.code === 0 ? staticLinkRes.data.product_intro : [],
      productShoppingNotices: staticLinkRes.code === 0 ? staticLinkRes.data.purchase_notice : [],
      timezone: res.timezone ?? '',
    }

    if (res.timezone) {
      commit('booking/SET_BOOKING_TIME_ZONE', res.timezone, { root: true })
    }

    commit('SET_PRODUCT', productResult)

    return {
      ...res,
      data: productResult,
    }
  },
  async getProductList({ commit }, params) {
    const res = await useApiClient().$get(`/pub/product`, { params: params })
    if (res.code !== 0) {
      throw res
    }
    const products = res.data.filter(product => {
      return product.sales_mode !== ProductSalesMode.WineDelivery || product.sales_mode !== ProductSalesMode.Movie
    })
    commit('SET_CATEGORY_PRODUCT_INFO', { products: products, totalCount: res.total_count })
    return res
  },
  getFlashProductList({ commit }, { regionID, sortby }) {
    // 限時促銷固定 10782 (任何環境下)
    const flashParams = {
      start: 0,
      sortby: sortby || null,
      filter_regionid: regionID,
      category_id: 10782,
    }
    return useApiClient()
      .$get(`/pub/product`, { params: flashParams })
      .then(res => {
        return res
      })
      .catch(error => {
        console.error('getFlashProductList: error', error)
        return error
      })
  },
  async getOtherProducts({ commit }, { pid }) {
    const res = await useApiClient().$get(`/pub/product/${pid}/other-products`)
    if (res.code === 0) {
      // sales_mode: 4 外送酒訂單
      const products = res.data.filter(product => {
        return product.sales_mode !== 4
      })
      commit('SET_OTHER_PRODUCTS', products)
      return res.data
    }
  },
  getProductHeadCount({ commit }, { pid }) {
    return new Promise((resolve, reject) => {
      const apiUrl = `/pub/product/${pid}/headcount`
      useApiClient()
        .$get(apiUrl)
        .then(res => {
          resolve(res)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getBookingEngineCount({ commit }, { bid, pid, skipResource, isBookingEngine }) {
    return new Promise((resolve, reject) => {
      const apiPath = `/branch/${bid}/headcount`
      let query = {
        pid: pid,
        // skip_resource: skipResource
      }
      if (isBookingEngine) {
        query = {
          ...query,
          for_booking_engine: isBookingEngine,
        }
      }
      useApiClient()
        .$get(apiPath, { params: query, platform: 'FunBook' })
        .then(res => {
          if (res.code === 0) {
            resolve(res)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  async getMaxCrossDays({ commit }, { pid, count, arrivalTime }) {
    const apiUrl = `/pub/product/${pid}/maxcrossdays`
    try {
      const res = await useApiClient().$get(apiUrl, { params: { count: count, arrival_time: arrivalTime } })
      commit('SET_MAX_CROSS_DAYS', res.data.day_prices || [])
      commit('SET_DAY_PRICES', res.data.day_prices || [])
      return res
    } catch (error) {
      console.error(error)
      throw error
    }
  },
  getArrivalTimes({ commit }, { pid, count, timeSlotsType }) {
    return new Promise((resolve, reject) => {
      const apiUrl = `/pub/product/${pid}/arrivaltimes`
      /** @type {import('@nuxtjs/axios').NuxtAxiosInstance} */
      const axios = useApiClient()
      axios
        .$get(apiUrl, { params: { count, time_slots_type: timeSlotsType }, headers: { APIVer: 2 } })
        .then(res => {
          const responseData = res.data
          const dataKey = timeSlotsType === BookingTimeSlotType.TIME_BLOCK ? 'selldates' : 'data'
          const isSoldOut = responseData[dataKey] === undefined || responseData[dataKey] === null
          if (res.code === 329) {
            resolve(res)
          } else if (isSoldOut) {
            resolve({ code: 47, message: useNuxtApp().$i18n.t('sold_out_msg') })
          }
          if (res.code === 0 && !isSoldOut) {
            const arrivalTimes = middlewareArrivalTimes.call(this, responseData.data, {
              minPrice: responseData.min_price,
              srrp: responseData.srrp,
            })
            const data = Object.assign({}, responseData, {
              arrivalTimes: arrivalTimes.dateTimes,
              bestOffPeak: arrivalTimes.bestOffPeak,
            })
            resolve({ ...res, data: data })
          }
          resolve(responseData)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getBookingEngineArrivalTimes({ commit }, { bid, pid, count }) {
    return new Promise((resolve, reject) => {
      const apiPath = `/branch/${bid}/arrivaltimes`
      useApiClient()
        .$get(apiPath, { params: { count: count, pid: pid, for_booking_engine: true }, platform: 'FunBook' })
        .then(res => {
          const responseData = res.data
          if (res.code === 0 && responseData.data !== null) {
            const sellDates = responseData.selldates
            let arrivalTimes = {}
            Object.keys(sellDates).forEach(date => {
              let dateResult = []
              sellDates[date].forEach(timeRangeObj => {
                timeRangeObj.selltimes.forEach(selltimeObj => {
                  dateResult = [
                    ...dateResult,
                    {
                      promostart: selltimeObj.selltime,
                      discount: selltimeObj.price,
                      ...getMoreDateInfo.call(this, selltimeObj.selltime),
                    },
                  ]
                })
              })
              arrivalTimes = {
                ...arrivalTimes,
                [date]: dateResult,
              }
            })
            resolve({ ...res, data: arrivalTimes })
          }
          resolve(responseData)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getProductCommentTagList({ commit }, pid) {
    if (pid) {
      const url = `/pub/product/${pid}/review-tags-count`
      useApiClient()
        .$get(url)
        .then(res => {
          if (res.code === 0) {
            commit('SET_PRODUCT_COMMENT_TAGS_LIST', res.data)
          }
        })
    }
  },
}

function getMoreDateInfo(selltime) {
  const promostart = useNuxtApp().$dayjs(selltime)
  // const date = promostart.format('YYYY-MM-DD')
  return {
    date: promostart.format('MM-DD'),
    time: promostart.format('HH:mm'),
    meridiem: promostart.format('A'),
  }
}

function middlewareArrivalTimes(arrivalTimesdata = [], { minPrice, srrp }) {
  const dateTimesObj = {}
  const dateBestOffPeakObj = {}
  arrivalTimesdata.forEach(arrivalData => {
    const promostart = useNuxtApp().$dayjs(arrivalData.promostart)
    const date = promostart.format('YYYY-MM-DD')
    if (!dateTimesObj[date]) {
      dateTimesObj[date] = []
      dateBestOffPeakObj[date] = false
    }
    const arrivalTimeData = Object.assign({}, arrivalData, {
      date: promostart.format('MM-DD'),
      time: promostart.format('HH:mm'),
      selltime: promostart.format('YYYY-MM-DD HH:mm:ss'), // for time block data
      meridiem: promostart.format('A'),
    })
    dateTimesObj[date].push(arrivalTimeData)
    // There is a min_price at the "Date" and the price is less than SRRP
    dateBestOffPeakObj[date] = dateBestOffPeakObj[date] ? true : arrivalData.discount === minPrice && arrivalData.discount < srrp
  })
  return { dateTimes: dateTimesObj, bestOffPeak: dateBestOffPeakObj }
}

export const mutations = {
  SET_PRODUCT(state, productInfo) {
    state.productInfo = productInfo
  },
  SET_PRODUCT_LIST(state, productList) {
    state.productList = productList
  },
  SET_OTHER_PRODUCTS(state, otherProducts) {
    state.otherProducts = otherProducts
  },
  SET_PRODUCT_COMMENT_TAGS_LIST(state, commentTagList) {
    state.commentTagsList = commentTagList.tags
  },
  /**
   * @param {{ date: string, discount: number }[]} maxCrossDays [{ date: '2022-05-16 10:00', discount: 100 }, ...]
   */
  SET_MAX_CROSS_DAYS(state, maxCrossDays) {
    const maxCrossDaysMap = maxCrossDays.reduce((dayMap, currentDay) => {
      const date = useNuxtApp().$dayjs(currentDay.date).format('YYYY-MM-DD')
      return {
        ...dayMap,
        [date]: currentDay.discount,
      }
    }, {})

    const lastMaxCrossDay = maxCrossDays?.[maxCrossDays.length - 1]?.date
    const checkOutDate = useNuxtApp().$dayjs(lastMaxCrossDay).add(1, 'day').format('YYYY-MM-DD')
    maxCrossDaysMap[checkOutDate] = 0

    state.maxCrossDays = maxCrossDaysMap
  },
  SET_DAY_PRICES(state, dayPrices) {
    state.dayPrices = dayPrices
  },
  SET_CATEGORY_PRODUCT_INFO(state, { products, totalCount }) {
    state.categoryProductInfo = Object.assign({}, { products: products, totalCount: totalCount })
  },
  RESET_MAX_CROSS_DAYS(state) {
    state.maxCrossDays = {}
  },
}
